import { z } from "zod";
import { managerColorSchema } from "./colors";
import { localeSchema } from "./locale";
import { roleSchema, userStateSchema } from "./roles";

/**
 * Local sign-in. Users are created by invitation, so there is no registration schema
 * here and never will be — see the book's user lifecycle.
 */
export const loginSchema = z.object({
  email: z.string().trim().toLowerCase().email("Enter a valid email address"),
  password: z.string().min(1, "Enter your password"),
});

export type LoginInput = z.infer<typeof loginSchema>;

/** Length over composition rules: a long passphrase beats a short scramble. */
export const PASSWORD_MIN_LENGTH = 12;
export const PASSWORD_MAX_LENGTH = 128;

export const passwordSchema = z
  .string()
  .min(PASSWORD_MIN_LENGTH, `Use at least ${PASSWORD_MIN_LENGTH} characters`)
  .max(PASSWORD_MAX_LENGTH, `Use at most ${PASSWORD_MAX_LENGTH} characters`);

/**
 * The signed-in user as the browser sees them.
 *
 * `state` and `credentialSet` are derived server-side and returned as fields — they
 * are never stored, and `passwordHash` cannot appear here by construction.
 *
 * `locale` rides the session because it is what the whole interface is rendered in: putting
 * it anywhere else would mean a second request before the first word could be drawn.
 */
export const sessionUserSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  email: z.string(),
  role: roleSchema,
  state: userStateSchema,
  credentialSet: z.boolean(),
  /** Which language this person reads the interface in. Never affects board collation. */
  locale: localeSchema,
  /**
   * Their manager colour, or null when the Head of Sales has not assigned one.
   *
   * On the session user purely so the shell's own avatar can wear it: the board cards, the
   * list and the detail headers already carry it from their own responses. Not a permission
   * or a setting — the same palette key, read from the same column.
   */
  color: managerColorSchema.nullable(),
});

export type SessionUser = z.infer<typeof sessionUserSchema>;

export const meResponseSchema = z.object({
  user: sessionUserSchema,
});

export type MeResponse = z.infer<typeof meResponseSchema>;

/** Only present when the API runs with `AUTH_MODE=dev`. */
/**
 * A development sign-in choice. Name and role only — the picker needs no email, and this
 * endpoint answers without a session, so it returns the least that makes it work.
 */
export const devUserSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  role: roleSchema,
});

export type DevUser = z.infer<typeof devUserSchema>;

export const devLoginSchema = z.object({
  userId: z.string().uuid(),
});
